import React, { useEffect, useState, useRef } from 'react';
import { Button, Icon } from '@unite-us/ui';
import urlState from 'src/common/urlState';
import { hashObject } from 'src/common/hashObject';
import PropTypes from 'prop-types';
import { browserHistory } from 'src/common/utils/browserHistory';
import { connect } from 'react-redux';
import _ from 'lodash';
import DialogV2 from 'common/modal/DialogV2';
import { Spinner } from 'common/spinners';
import useWorkqueueRowSelector from 'common/Workqueue/hooks/useWorkqueueRowSelector';
import { useToast } from '@unite-us/app-components';
import { useExternalScreenings, assignScreen, bulkAssignScreens } from 'src/actions/Screening/Group';
import ExternalScreeningsTable from 'src/components/Dashboard/components/Screenings/ExternalScreeningsTable';
import AssignableScreenings from 'src/components/Dashboard/components/Screenings/AssignableScreenings';
import FilterBar from 'src/components/Dashboard/components/FilterBar';

let lastFilters = 0;

function ExternalScreenings(props) {
  urlState.sync();
  const initialPage = urlState.get('page', [1]).filter((v) => !!v).map((v) => parseInt(v, 10))[0] || 1;
  const [page, setPageState] = useState(initialPage);
  const setPage = (val) => {
    setPageState(val);
    urlState.set('page', val);
  };
  const [{ sortBy, sortDirection }, setSort] = useState({
    sortBy: 'updated_at',
    sortDirection: 'desc',
  });

  const setFilter = (filter) => {
    const matches = filter.options
      .filter((opt) => opt.initial === true)
      .map((opt) => opt.value);
    if (!matches || matches.length === 0) urlState.remove(filter.key);
    else urlState.set(filter.key, ...matches);
  };

  const [filters, setFiltersState] = useState([]);
  const setFilters = (val) => {
    setFiltersState(val);
    val.forEach(setFilter);
  };
  const [templates, setTemplates] = useState([]);

  const { data, isFetching, refetch } = useExternalScreenings(
    {
      page,
      filters,
      sortBy,
      sortDirection,
      templates,
    },
    props.providerId,
  );

  const { screenings, paging } = data || { screenings: [], paging: {} };
  const maxPage = Math.ceil((paging.total_count || 1) / (paging.per || 1));
  const pageLessThanZero = page < 0;
  const pageTooLarge = !!paging.per && page > maxPage;
  if (pageLessThanZero || pageTooLarge) setPage(1);

  const mapFilterOptions = (filter, values) => {
    const fn = (opt) => ({ ...opt, initial: values.indexOf(opt.value) > -1 });
    return filter.options.map(fn);
  };

  const makeMappedFilter = (filter, values) => ({
    ...filter, options: mapFilterOptions(filter, values),
  });

  const mapFilter = (filter, key, values) => (filter.key === key ? makeMappedFilter(filter, values) : filter);

  const setFilterInitial = (fs, key) => {
    const values = urlState.get(key);
    if (!values) return fs;
    return fs.map((filter) => mapFilter(filter, key, values));
  };

  useEffect(() => {
    if (data?.filters) {
      let fs = setFilterInitial(data.filters, 'is_assigned');
      fs = setFilterInitial(fs, 'source');
      fs = setFilterInitial(fs, 'type');
      setFilters(fs);
    }

    if (data?.templates) setTemplates(data.templates);
  }, [data?.filters]);

  const onCellClick = (screen) => {
    if (screen) {
      const screenId = screen.id;
      const clientId = screen.subject.id;
      browserHistory.push(
        `/screenings/v2/facesheet/${clientId}/submission/${screenId}`,
      );
    }
  };

  useEffect(() => {
    if (!filters || filters.length === 0) return;
    const hash = hashObject(filters);
    if (lastFilters !== 0 && hash !== lastFilters) setPage(1);
    lastFilters = hash;
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [filters, page, sortBy, sortDirection]);

  const onFiltersChange = (key, newFilters) => {
    const updatedFilters = filters.map((filter) => {
      if (filter.key === key) {
        return {
          ...filter,
          options: filter.options.map((option) => ({
            ...option,
            initial: newFilters.includes(option.value),
          })),
        };
      }
      return filter;
    });

    setFilters(updatedFilters);
  };

  const onNextClick = () => {
    setPage(page + 1);
  };

  const onPrevClick = () => {
    setPage(page - 1);
  };

  const setSorting = (sort) => {
    setSort(sort);
  };

  const rowSelectorProps = useWorkqueueRowSelector({ pageRows: screenings });

  const [assigningScreens, setAssigningScreens] = useState({});
  const [assignableSelectedIds, setAssignableSelectedIds] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const assignDialogRef = useRef(null);
  const { addToast } = useToast();

  const onCheckAssign = async (screenId, assign) => {
    if (assigningScreens[screenId]) return;

    setAssigningScreens((prev) => ({ ...prev, [screenId]: true }));

    try {
      const assigned = await assignScreen(screenId, assign);
      if (assigned) {
        await refetch();
      }
    } finally {
      setAssigningScreens((prev) => ({ ...prev, [screenId]: false }));
    }
  };

  const unassignedSelectedScreenings = rowSelectorProps.selectedRows.filter(
    (screen) => screen.assigned_to_id === null,
  );

  const toggleAssignableSelection = (screenId) => {
    setAssignableSelectedIds((prev) => (prev.includes(screenId) ?
        prev.filter((id) => id !== screenId) :
        [...prev, screenId]));
  };

  const isAllSelected = assignableSelectedIds.length === unassignedSelectedScreenings.length;

  const toggleAllAssignableSelection = () => {
    if (isAllSelected) {
      setAssignableSelectedIds([]);
    } else {
      setAssignableSelectedIds(unassignedSelectedScreenings.map((screen) => screen.id));
    }
  };

  const openAssignDialog = () => {
    setAssignableSelectedIds(unassignedSelectedScreenings.map((screen) => screen.id));
    assignDialogRef.current.openDialog();
  };

  const bulkAssign = async () => {
    setIsAssigning(true);
    const assigned = await bulkAssignScreens(assignableSelectedIds);
    if (assigned) {
      await refetch();
      rowSelectorProps.deselectAll();
      assignDialogRef.current.closeDialog();
      addToast({
                message: `${assignableSelectedIds.length} screenings marked as Assigned`,
                type: 'success',
              });
    }
    setIsAssigning(false);
  };

  return (
    <div>
      <FilterBar
        filters={_.sortBy(filters, 'name')}
        onFiltersChange={onFiltersChange}
        paging={paging}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
        isFetching={isFetching && !_.isEmpty(screenings)}
      />
      {rowSelectorProps.selectedRows.length > 0 && (
        <div className="filter-bar bg-dark-fill-blue">
          <div className="filters mr-5">
            <div className="border-b px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium">
                    {rowSelectorProps.selectedRows.length} screenings selected.
                  </span>
                  <Button
                    id="new-screening-submission"
                    label="Assign Selected"
                    labelposition={'after'}
                    iconLeft={<Icon icon="IconCheck" className="fill-current" />}
                    primary
                    onClick={() => openAssignDialog()}
                  />
                </div>
                <Button
                  id="new-screening-submission"
                  label="Clear Selection"
                  labelposition={'after'}
                  iconLeft={<Icon icon="IconCross" className="fill-current" />}
                  secondary
                  onClick={() => rowSelectorProps.deselectAll()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <ExternalScreeningsTable
        screenings={screenings}
        isFetching={isFetching}
        sortBy={sortBy}
        sortDirection={sortDirection}
        setSort={setSorting}
        paging={paging}
        onCellClick={onCellClick}
        onCheckAssign={onCheckAssign}
        toggleSelectedRow={rowSelectorProps.toggleSelectedRow}
        toggleSelectAllRows={rowSelectorProps.toggleSelectAllRows}
        selectedRows={rowSelectorProps.selectedRows}
        assigningScreens={assigningScreens}
      />
      <DialogV2
        ref={assignDialogRef}
        title="Mark Screenings as Assigned"
        confirmLabel={`Mark as Assigned (${assignableSelectedIds.length})`}
        cancelHandler={() => assignDialogRef.current.closeDialog()}
        confirmationHandler={() => bulkAssign()}
        confirmationBtnDisabled={assignableSelectedIds.length === 0 || isAssigning}
        width="3xl"
      >
        {isAssigning ? <Spinner /> : (
          <AssignableScreenings
            screenings={unassignedSelectedScreenings}
            selectedScreenIds={assignableSelectedIds}
            onToggleSelect={toggleAssignableSelection}
            onToggleAll={toggleAllAssignableSelection}
          />
      )}

      </DialogV2>
    </div>
  );
}

ExternalScreenings.propTypes = {
  providerId: PropTypes.string.isRequired,
};

ExternalScreenings.defaultProps = {};

function mapStateToProps(state) {
  return {
    providerId: state.session.groupId,
  };
}

export default connect(mapStateToProps)(ExternalScreenings);
